import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useWrite } from "../api/hooks";
import type { Appliance, ApplianceStatus, Station } from "../api/types";
import { can } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { StatusBadge } from "../components/Badges";
import { Dialog } from "../components/Dialog";
import { APPLIANCE_STATUS, ASSIGNMENT_END, BOOKING_END, labelOf, statusOf } from "../components/labels";
import { Notice, ProblemAlert, QueryState, UnknownUnkeyed } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { formatDateTime, lusakaLocalToUtc, shortId } from "../format/datetime";
import { lonLat, personLabel, ScreenHeader, useConfigNames, useStationNames, useWardNames } from "./shared";
import { Button } from "@lcc/ui";

type Tab = "stations" | "appliances" | "assignments";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PHONE = /^\+[1-9][0-9]{7,14}$/;

function coords(lon: string | undefined, lat: string | undefined): { lon: number; lat: number } | string {
  const x = Number(lon);
  const y = Number(lat);
  if (!lon?.trim() || !lat?.trim() || !Number.isFinite(x) || !Number.isFinite(y) || x < -180 || x > 180 || y < -90 || y > 90)
    return "Give longitude (-180 to 180) and latitude (-90 to 90).";
  return { lon: x, lat: y };
}

function StationsTab() {
  const { principal } = useFireContext();
  const wardName = useWardNames();
  const q = useApi(["stations", {}], (api) => endpoints.listStations(api));
  const [dialog, setDialog] = useState<{ kind: "add" } | { kind: "edit"; s: Station } | { kind: "view"; s: Station } | null>(null);
  const detail = useApi(["station", dialog?.kind === "view" ? dialog.s.station_id : ""], (api) => endpoints.getStation(api, dialog?.kind === "view" ? dialog.s.station_id : ""), {
    enabled: dialog?.kind === "view" && can(principal, "viewStation"),
  });
  const rows = q.data ?? [];
  return (
    <>
      {can(principal, "createStation") && (
        <div className="fer-actions fer-actions--start">
          <Button onClick={() => setDialog({ kind: "add" })}>
            Add a station
          </Button>
        </div>
      )}
      <QueryState isLoading={q.isLoading} error={q.error} label="stations" />
      {rows.length > 0 && (
        <table className="lcc-table" aria-label={`Stations (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">Station</th>
              <th scope="col">Ward</th>
              <th scope="col">Position</th>
              <th scope="col">Phone</th>
              <th scope="col">Active</th>
              <th scope="col">
                <span className="lcc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.station_id}>
                <th scope="row">
                  {s.name} ({s.code})
                </th>
                <td>{wardName(s.ward_id)}</td>
                <td>
                  {lonLat(s.lon, s.lat)}
                  {s.plus_code ? ` (${s.plus_code})` : ""}
                </td>
                <td>{s.phone ?? "—"}</td>
                <td>{s.active ? "Active" : "Inactive (no new appliances or assignments)"}</td>
                <td className="fer-row-actions">
                  {can(principal, "viewStation") && (
                    <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "view", s })} aria-label={`Appliances of ${s.code}`}>
                      Appliances
                    </Button>
                  )}
                  {can(principal, "updateStation") && (
                    <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "edit", s })} aria-label={`Edit ${s.code}`}>
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog?.kind === "add" && (
        <ActionDialog
          title="Add a station"
          intent="createStation:new"
          fields={[
            { name: "code", label: "Code (A-Z, 0-9, dash)", required: true, maxLength: 16 },
            { name: "name", label: "Name", required: true, maxLength: 120 },
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "address", label: "Address", maxLength: 300 },
            { name: "phone", label: "Station phone (+260...)", maxLength: 20 },
          ]}
          submitLabel="Add station"
          build={(v) => {
            const code = v.code.trim().toUpperCase();
            if (!/^[A-Z0-9-]{2,16}$/.test(code)) return { error: "The code has 2 to 16 capital letters, digits or dashes." };
            if (v.name.trim().length < 2) return { error: "The name has at least 2 characters." };
            const c = coords(v.lon, v.lat);
            if (typeof c === "string") return { error: c };
            if (v.phone?.trim() && !PHONE.test(v.phone.trim())) return { error: "Give the phone in international form, e.g. +260211123456." };
            return { body: { code, name: v.name.trim(), ...c, address: v.address?.trim() || null, phone: v.phone?.trim() || null } };
          }}
          write={(api, body, key) => endpoints.createStation(api, body as never, key)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "edit" && (
        <ActionDialog
          title={`Edit station ${dialog.s.code}`}
          initial={{
            name: dialog.s.name,
            lon: String(dialog.s.lon),
            lat: String(dialog.s.lat),
            address: dialog.s.address ?? "",
            phone: dialog.s.phone ?? "",
            active: String(dialog.s.active),
          }}
          fields={[
            { name: "name", label: "Name", required: true, maxLength: 120 },
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "address", label: "Address", maxLength: 300 },
            { name: "phone", label: "Station phone (+260...)", maxLength: 20 },
            { name: "active", label: "Active (takes appliances and assignments)", kind: "checkbox" },
          ]}
          submitLabel="Save"
          build={(v) => {
            const s = dialog.s;
            const body: Record<string, unknown> = {};
            if (v.name.trim() !== s.name) body.name = v.name.trim();
            const c = coords(v.lon, v.lat);
            if (typeof c === "string") return { error: c };
            if (c.lon !== s.lon || c.lat !== s.lat) Object.assign(body, c);
            if ((v.address?.trim() || null) !== (s.address ?? null)) body.address = v.address?.trim() || null;
            if (v.phone?.trim() && !PHONE.test(v.phone.trim())) return { error: "Give the phone in international form, e.g. +260211123456." };
            if ((v.phone?.trim() || null) !== (s.phone ?? null)) body.phone = v.phone?.trim() || null;
            if ((v.active === "true") !== s.active) body.active = v.active === "true";
            return Object.keys(body).length ? { body } : { error: "Nothing has changed." };
          }}
          write={(api, body) => endpoints.updateStation(api, dialog.s.station_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "view" && (
        <Dialog title={`Appliances of ${dialog.s.name}`} onClose={() => setDialog(null)}>
          <QueryState isLoading={detail.isLoading} error={detail.error} label="the station" />
          {detail.data && (
            <ul className="fer-plain-list" aria-label={`Appliances of ${dialog.s.code}`}>
              {detail.data.appliances.length === 0 && <li className="fer-muted">No appliance.</li>}
              {detail.data.appliances.map((a) => (
                <li key={a.appliance_id}>
                  {a.call_sign} <StatusBadge status={statusOf(APPLIANCE_STATUS, a.status)} />
                  {a.retired ? " (retired)" : ""}
                </li>
              ))}
            </ul>
          )}
          <div className="fer-actions">
            <Button onClick={() => setDialog(null)}>
              Close
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}

function ServiceStatusDialog({ a, onClose }: { a: Appliance; onClose: () => void }) {
  const names = useConfigNames();
  const back = a.status === "out_of_service";
  return (
    <ActionDialog
      title={back ? `Put ${a.call_sign} back in service` : `Put ${a.call_sign} out of service`}
      intro={
        a.status === "dispatched" && !back ? (
          <Notice tone="warning">
            {a.call_sign} is dispatched: its dispatch is stood down and a backup request is raised on the incident, so the dispatcher sends
            another appliance.
          </Notice>
        ) : undefined
      }
      fields={
        back
          ? [{ name: "note", label: "Note", kind: "textarea", maxLength: 1000 }]
          : [
              {
                name: "reason_key",
                label: "Why",
                kind: "select",
                required: true,
                options: (names.staff?.out_of_service_reasons ?? []).map((r) => [r.key, names.oosReason(r.key)] as const),
              },
              { name: "expected_back_at", label: "Expected back (Lusaka time)", kind: "datetime" },
              { name: "note", label: "Note", kind: "textarea", maxLength: 1000 },
            ]
      }
      submitLabel={back ? "Back in service" : "Out of service"}
      build={(v) => {
        if (back) return { body: v.note?.trim() ? { in_service: true, note: v.note.trim() } : { in_service: true } };
        const body: Record<string, unknown> = { in_service: false, reason_key: v.reason_key };
        if (v.expected_back_at) {
          const at = lusakaLocalToUtc(v.expected_back_at);
          if (!at) return { error: "Give when it is expected back as a date and time." };
          body.expected_back_at = at;
        }
        if (v.note?.trim()) body.note = v.note.trim();
        return { body };
      }}
      write={(api, body) => endpoints.setServiceStatus(api, a.appliance_id, body as never)}
      onClose={onClose}
    />
  );
}

function capabilitiesOf(value: string | undefined, known: string[]): string[] | string {
  const list = Array.from(new Set((value ?? "").split(",").map((x) => x.trim()).filter(Boolean)));
  if (list.length < 1 || list.length > 10) return "Give 1 to 10 capabilities, separated by commas.";
  const unknown = known.length ? list.filter((c) => !known.includes(c)) : [];
  return unknown.length ? `Not a configured capability: ${unknown.join(", ")}.` : list;
}

function AppliancesTab() {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const stationName = useStationNames();
  const stations = useApi(["stations", {}], (api) => endpoints.listStations(api));
  const [station, setStation] = useState("");
  const [status, setStatus] = useState<ApplianceStatus | "">("");
  const q = useApi(["appliances", station, status], (api) => endpoints.listAppliances(api, { stationId: station || undefined, status: status ? [status] : undefined }), { keepPrevious: true });
  const [dialog, setDialog] = useState<{ kind: "add" } | { kind: "edit"; a: Appliance } | { kind: "service"; a: Appliance } | null>(null);
  const known = (names.staff?.capabilities ?? []).map((c) => c.key);
  const rows = q.data ?? [];
  const stationOptions = (stations.data ?? []).map((s) => [s.station_id, `${s.name} (${s.code})`] as const);
  return (
    <>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Station</span>
          <select value={station} onChange={(e) => setStation(e.target.value)}>
            <option value="">All</option>
            {stationOptions.map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="fer-field fer-field--inline">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as ApplianceStatus | "")}>
            <option value="">All</option>
            {Object.entries(APPLIANCE_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
        {can(principal, "createAppliance") && (
          <Button onClick={() => setDialog({ kind: "add" })}>
            Add an appliance
          </Button>
        )}
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} label="appliances" />
      {q.data && rows.length === 0 && <p className="fer-muted">No appliance matches.</p>}
      {rows.length > 0 && (
        <table className="lcc-table" aria-label={`Appliances (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">Call sign</th>
              <th scope="col">Station</th>
              <th scope="col">Capabilities</th>
              <th scope="col">Status</th>
              <th scope="col">Booked on</th>
              <th scope="col">Out of service</th>
              <th scope="col">
                <span className="lcc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.appliance_id}>
                <th scope="row">
                  {a.call_sign}
                  {a.retired ? " (retired)" : ""}
                  {a.water_capacity_litres ? <span className="fer-muted"> {a.water_capacity_litres} l</span> : null}
                </th>
                <td>{stationName(a.home_station_id)}</td>
                <td>{a.capabilities.map(names.capability).join(", ")}</td>
                <td>
                  <StatusBadge status={statusOf(APPLIANCE_STATUS, a.status)} />
                </td>
                <td>
                  {a.booking ? (
                    <>
                      {a.booking.display_name ?? shortId(a.booking.party_id)} since {formatDateTime(a.booking.started_at)}
                      {a.booking.crew_count ? `, crew of ${a.booking.crew_count}` : ""}
                      {a.booking.end_reason ? ` (${labelOf(BOOKING_END, a.booking.end_reason)})` : ""}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {a.out_of_service ? (
                    <>
                      {names.oosReason(a.out_of_service.reason_key)} since {formatDateTime(a.out_of_service.since)}
                      {a.out_of_service.expected_back_at ? `, back ${formatDateTime(a.out_of_service.expected_back_at)}` : ""}
                      {a.out_of_service.note ? ` – ${a.out_of_service.note}` : ""}
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="fer-row-actions">
                  {!a.retired && can(principal, "setServiceStatus") && (
                    <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "service", a })} aria-label={`${a.status === "out_of_service" ? "Back in service" : "Out of service"}: ${a.call_sign}`}>
                      {a.status === "out_of_service" ? "Back in service" : "Out of service"}
                    </Button>
                  )}
                  {can(principal, "updateAppliance") && (
                    <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "edit", a })} aria-label={`Edit ${a.call_sign}`}>
                      Edit
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog?.kind === "service" && <ServiceStatusDialog a={dialog.a} onClose={() => setDialog(null)} />}
      {dialog?.kind === "add" && (
        <ActionDialog
          title="Add an appliance"
          intent="createAppliance:new"
          fields={[
            { name: "call_sign", label: "Call sign (A-Z, 0-9, dash)", required: true, maxLength: 16 },
            { name: "home_station_id", label: "Home station", kind: "select", required: true, options: stationOptions },
            { name: "capabilities", label: `Capabilities, comma separated (${known.join(", ") || "configured keys"})`, required: true },
            { name: "water_capacity_litres", label: "Water capacity (litres)", kind: "number", min: "0", max: "50000" },
            { name: "registration_plate", label: "Registration plate", maxLength: 12 },
            { name: "make_model", label: "Make and model", maxLength: 100 },
          ]}
          submitLabel="Add appliance"
          build={(v) => {
            const call = v.call_sign.trim().toUpperCase();
            if (!/^[A-Z0-9-]{2,16}$/.test(call)) return { error: "The call sign has 2 to 16 capital letters, digits or dashes." };
            const caps = capabilitiesOf(v.capabilities, known);
            if (typeof caps === "string") return { error: caps };
            const water = v.water_capacity_litres ? Number(v.water_capacity_litres) : null;
            if (water !== null && (!Number.isInteger(water) || water < 0 || water > 50000)) return { error: "Water capacity: a whole number of litres up to 50000." };
            return {
              body: {
                call_sign: call,
                home_station_id: v.home_station_id,
                capabilities: caps,
                water_capacity_litres: water,
                registration_plate: v.registration_plate?.trim() || null,
                make_model: v.make_model?.trim() || null,
              },
            };
          }}
          write={(api, body, key) => endpoints.createAppliance(api, body as never, key)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "edit" && (
        <ActionDialog
          title={`Edit appliance ${dialog.a.call_sign}`}
          initial={{
            call_sign: dialog.a.call_sign,
            home_station_id: dialog.a.home_station_id,
            capabilities: dialog.a.capabilities.join(","),
            water_capacity_litres: dialog.a.water_capacity_litres == null ? "" : String(dialog.a.water_capacity_litres),
            retired: String(dialog.a.retired),
          }}
          fields={[
            { name: "call_sign", label: "Call sign", required: true, maxLength: 16 },
            { name: "home_station_id", label: "Home station", kind: "select", required: true, options: stationOptions },
            { name: "capabilities", label: "Capabilities, comma separated", required: true },
            { name: "water_capacity_litres", label: "Water capacity (litres)", kind: "number", min: "0", max: "50000" },
            { name: "retired", label: "Retired (never dispatched again)", kind: "checkbox" },
          ]}
          submitLabel="Save"
          build={(v) => {
            const a = dialog.a;
            const body: Record<string, unknown> = {};
            const call = v.call_sign.trim().toUpperCase();
            if (!/^[A-Z0-9-]{2,16}$/.test(call)) return { error: "The call sign has 2 to 16 capital letters, digits or dashes." };
            if (call !== a.call_sign) body.call_sign = call;
            if (v.home_station_id !== a.home_station_id) body.home_station_id = v.home_station_id;
            const caps = capabilitiesOf(v.capabilities, known);
            if (typeof caps === "string") return { error: caps };
            if (caps.join(",") !== a.capabilities.join(",")) body.capabilities = caps;
            const water = v.water_capacity_litres ? Number(v.water_capacity_litres) : null;
            if (water !== null && (!Number.isInteger(water) || water < 0 || water > 50000)) return { error: "Water capacity: a whole number of litres up to 50000." };
            if (water !== (a.water_capacity_litres ?? null)) body.water_capacity_litres = water;
            if ((v.retired === "true") !== a.retired) body.retired = v.retired === "true";
            return Object.keys(body).length ? { body } : { error: "Nothing has changed." };
          }}
          write={(api, body) => endpoints.updateAppliance(api, dialog.a.appliance_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
}

function AssignmentsTab() {
  const { principal } = useFireContext();
  const stationName = useStationNames();
  const stations = useApi(["stations", {}], (api) => endpoints.listStations(api));
  const [ended, setEnded] = useState(false);
  const q = useApi(["assignments", ended], (api) => endpoints.listAssignments(api, { includeEnded: ended }), { keepPrevious: true });
  const [adding, setAdding] = useState(false);
  const end = useWrite((api, id: string) => endpoints.endAssignment(api, id));
  const rows = q.data ?? [];
  return (
    <>
      <p className="fer-hint">
        A crew commander acts only for the appliances of the stations they are assigned to (no assignment, no access). Ending an
        assignment ends their booking there once any active dispatch is over.
      </p>
      <div className="fer-toolbar">
        <label className="fer-check">
          <input type="checkbox" checked={ended} onChange={(e) => setEnded(e.target.checked)} />
          Include ended assignments
        </label>
        {can(principal, "createAssignment") && (
          <Button onClick={() => setAdding(true)}>
            Assign a crew commander
          </Button>
        )}
      </div>
      <ProblemAlert error={end.error} />
      <UnknownUnkeyed show={end.unknown} />
      <QueryState isLoading={q.isLoading} error={q.error} label="assignments" />
      {q.data && rows.length === 0 && <p className="fer-muted">No assignment.</p>}
      {rows.length > 0 && (
        <table className="lcc-table" aria-label={`Station assignments (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">Crew commander</th>
              <th scope="col">Station</th>
              <th scope="col">Since</th>
              <th scope="col">Ended</th>
              <th scope="col">
                <span className="lcc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.assignment_id}>
                <th scope="row">{a.display_name ?? `staff ${shortId(a.party_id)}`}</th>
                <td>{stationName(a.station_id)}</td>
                <td>
                  {formatDateTime(a.started_at)} by {personLabel(a.created_by, principal.partyId)}
                </td>
                <td>{a.ended_at ? `${formatDateTime(a.ended_at)} (${labelOf(ASSIGNMENT_END, a.end_reason)})` : "—"}</td>
                <td>
                  {!a.ended_at && can(principal, "endAssignment") && (
                    <Button variant="secondary" size="sm" disabled={end.isPending} onClick={() => end.mutate(a.assignment_id)} aria-label={`End the assignment of ${a.display_name ?? shortId(a.party_id)}`}>
                      End
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {adding && (
        <ActionDialog
          title="Assign a crew commander to a station"
          intent="createAssignment:new"
          intro={<p>The person must hold the crew commander role (granted in the council's people register).</p>}
          fields={[
            { name: "station_id", label: "Station", kind: "select", required: true, options: (stations.data ?? []).filter((s) => s.active).map((s) => [s.station_id, `${s.name} (${s.code})`] as const) },
            { name: "party_id", label: "The person's party id", required: true, maxLength: 36 },
          ]}
          submitLabel="Assign"
          build={(v) => (UUID.test(v.party_id.trim()) ? { body: { station_id: v.station_id, party_id: v.party_id.trim() } } : { error: "The party id is a UUID (36 characters)." })}
          write={(api, body, key) => endpoints.createAssignment(api, body as never, key)}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}

/** Stations, appliances (readiness, service status) and crew commanders' station assignments. */
export function ResourcesScreen() {
  const { principal } = useFireContext();
  const tabs = [
    ...(can(principal, "viewStations") ? [{ key: "stations" as const, label: "Stations" }] : []),
    ...(can(principal, "viewAppliances") ? [{ key: "appliances" as const, label: "Appliances" }] : []),
    ...(can(principal, "viewAssignments") ? [{ key: "assignments" as const, label: "Assignments" }] : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? "stations");
  const current = tabs.some((t) => t.key === tab) ? tab : tabs[0]?.key;
  return (
    <div className="fer-screen">
      <ScreenHeader title="Stations and appliances" />
      <Tabs label="Resources" tabs={tabs} current={current ?? "stations"} onChange={setTab}>
        {current === "stations" && <StationsTab />}
        {current === "appliances" && <AppliancesTab />}
        {current === "assignments" && <AssignmentsTab />}
      </Tabs>
    </div>
  );
}
